
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { toEmpfaengerListe } from '../../../lib/emails/empfaenger';
import { eigentuemerAnzeigename } from '../../../lib/eigentuemer/anzeige';
import {
  ArrowBendDoubleUpLeft, ArrowBendUpLeft, ArrowBendUpRight, Buildings, CalendarPlus, CaretDown, CaretUp, CheckCircle, CircleNotch, Clock, CornersIn, CornersOut,
  DownloadSimple, EnvelopeOpen, EnvelopeSimple, File, FileDoc, FileImage, FilePdf, FileText, FileXls, LinkSimple, PaperPlaneTilt, Receipt, Star,
  Trash, User, UserCheck, UserPlus, Users, X, type Icon,
} from '@phosphor-icons/react';
import { BestaetigenDialog, Chip, ImmoButton } from '../../../components/immobilien/ui';
import { initialen } from '../../../components/immobilien/email/listenAnsicht';
import { WiedervorlageWahl } from './bereich/liste/EmailKontextmenue';
import { antwortEmpfaenger } from '../../../lib/emails/antwortEmpfaenger';
import { parseAbsender, baueKontaktPrefill, type KontaktPrefill } from '../../../lib/emails/kontaktAusMail';
import { mailKlartext } from '../../../lib/emails/klartext';
import { terminAusMail, type TerminAusMail } from '../../../lib/emails/terminAusMail';
import { erledigtText } from '../../../lib/emails/erledigtInfo';
import KiAnalysePanel from './KiAnalysePanel';
import AblageKarte from './AblageKarte';
import { AblageVorschlag, ladeVorschlaegeFuerEmail } from '../../../lib/dokumente/ablageApi';
import AnhangVorschau from './AnhangVorschau';
import { ladeAnhangBlob } from '../../../lib/emails/anhangDatei';
import EmailHtmlAnsicht from './EmailHtmlAnsicht';
import { AnKollegenSendenDialog } from '../../../components/chat/AnKollegenSendenDialog';
import { useNotification } from '../../../contexts/NotificationContext';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EmailDetailsSidebarProps {
  email: any;
  /** Blättern und Vollbild im Lesefenster (GlassModal). */
  kopf?: { onVorherige?: () => void; onNaechste?: () => void; vollbild: boolean; onVollbild: () => void };
  /** Mobil: 44-px-Ziele, schmalere Ränder. */
  mobil?: boolean;
  onClose: () => void;
  onReply: () => void;
  /** „Allen antworten" — übrige Empfänger der Mail landen im Cc. */
  onReplyAll?: () => void;
  /** Eigene Postfach-Adresse — sie zählt nicht als „weiterer Empfänger". */
  eigeneAdresse?: string;
  onForward: () => void;
  onDeleted: () => void;
  onUpdated: () => void;
  onWiedervorlage?: (datumIso: string | null) => void;
  /** Erledigt-Umschalter der geöffneten Mail — dieselbe Aktion wie das Häkchen in der Liste. */
  onToggleErledigt?: () => void;
  onCreateTicket?: (data: { titel: string; beschreibung: string; objekt_id?: string; mieter_id?: string; kategorie_id?: string; prioritaet?: string }) => void;
  onCreateKontakt?: (initial: KontaktPrefill) => void;
  /** #570: Termin aus der Mail anlegen (Vorbelegung aus Betreff, Text und Zuordnung). */
  onCreateTermin?: (vorschlag: TerminAusMail) => void;
  ticketRefresh?: number;
  ticketKategorien?: { id: string; name: string; farbe: string | null }[];
  userProfile?: any;
  onChanged?: () => void;
}

interface Anhang {
  id: string;
  dateiname: string;
  dateipfad: string;
  dateityp: string | null;
  dateigroesse: number | null;
  created_at: string;
  graph_message_id: string | null;
  graph_attachment_id: string | null;
  storage_path: string | null;
  /** Ergebnis des Rechnungs-Imports für genau diesen Anhang. */
  rechnung_status: string | null;
  rechnung_fehler: string | null;
}

interface ThreadEmail {
  id: string;
  von: string;
  betreff: string;
  datum: string;
  richtung: 'eingang' | 'ausgang';
  gelesen: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatRelativeDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  if (diffH < 24) return `vor ${diffH} Std.`;
  if (diffD === 1) return 'Gestern';
  if (diffD < 7) return `vor ${diffD} Tagen`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatAbsoluteDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── Component ───────────────────────────────────────────────────────────────

const EmailDetailsSidebar: React.FC<EmailDetailsSidebarProps> = ({
  email, kopf, mobil = false, onClose, onReply, onReplyAll, eigeneAdresse, onForward, onDeleted, onUpdated, onWiedervorlage, onToggleErledigt, onCreateTicket, onCreateKontakt, onCreateTermin,
  ticketRefresh, ticketKategorien = [], userProfile, onChanged,
}) => {
  // Collapsible sections
  const [showInhalt, setShowInhalt] = useState(true);
  const [showAnhaenge, setShowAnhaenge] = useState(true);
  const [showZuordnung, setShowZuordnung] = useState(false);
  const [showThread, setShowThread] = useState(true);

  // Data
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [anhaengeLoading, setAnhaengeLoading] = useState(false);
  const [threadEmails, setThreadEmails] = useState<ThreadEmail[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  // Aufgeklappte Thread-Mails + nachgeladene Bodies (fetchThread lädt nur Header).
  const [threadOffen, setThreadOffen] = useState<Set<string>>(new Set());
  const [threadBodies, setThreadBodies] = useState<Record<string, { html: string | null; text: string | null } | 'loading'>>({});
  const [vorschlaege, setVorschlaege] = useState<Record<string, AblageVorschlag>>({});

  // Body (html_inhalt/text_inhalt) wird NICHT in der Liste geladen (Performance) —
  // hier für die geöffnete Mail einzeln nachgeladen.
  const [body, setBody] = useState<{ html: string | null; text: string | null } | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  // Zuordnung
  const [editZuordnung, setEditZuordnung] = useState(false);
  const [mieterSearch, setMieterSearch] = useState('');
  const [eigentuemerSearch, setEigentuemerSearch] = useState('');
  const [objektSearch, setObjektSearch] = useState('');
  const [mieterResults, setMieterResults] = useState<any[]>([]);
  const [eigentuemerResults, setEigentuemerResults] = useState<any[]>([]);
  const [objektResults, setObjektResults] = useState<any[]>([]);
  const [senderBekannt, setSenderBekannt] = useState<boolean | null>(null);
  useEffect(() => {
    let abbruch = false;
    (async () => {
      setSenderBekannt(null);
      if (email?.mieter_id || email?.eigentuemer_id) { if (!abbruch) setSenderBekannt(true); return; }
      const adr = parseAbsender(email?.von || '').email;
      if (!adr) { if (!abbruch) setSenderBekannt(true); return; }
      const [k1, k2] = await Promise.all([
        supabase.from('kontakte').select('id').ilike('email', adr).limit(1),
        supabase.from('kontakt_kanaele').select('id').ilike('wert', adr).limit(1),
      ]);
      if (!abbruch) setSenderBekannt(((k1.data?.length || 0) + (k2.data?.length || 0)) > 0);
    })();
    return () => { abbruch = true; };
  }, [email?.id]);
  const [zustTeams, setZustTeams] = useState<{ id: string; name: string }[]>([]);
  const [zustMembers, setZustMembers] = useState<{ id: string; full_name: string }[]>([]);
  // #573: Name zu erledigt_von – aus der Mitarbeiterliste, sonst einmal nachladen
  const [erledigtName, setErledigtName] = useState<string | null>(null);
  useEffect(() => {
    const id: string | null = email?.erledigt_von ?? null;
    if (!id) { setErledigtName(null); return; }
    const bekannt = zustMembers.find(m => m.id === id)?.full_name;
    if (bekannt) { setErledigtName(bekannt); return; }
    let aktiv = true;
    void supabase.from('profiles').select('full_name').eq('id', id).maybeSingle()
      .then(({ data }) => { if (aktiv) setErledigtName((data?.full_name as string | undefined) ?? null); });
    return () => { aktiv = false; };
  }, [email?.erledigt_von, zustMembers]);
  const [zuordnungSaving, setZuordnungSaving] = useState(false);

  // Actions
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null);
  // #549: Anhang als Verweis in den Team-Chat
  const [anKollegen, setAnKollegen] = useState<Anhang | null>(null);
  const { showToast } = useNotification();
  const [actionLoading, setActionLoading] = useState(false);
  const [showWiedervorlage, setShowWiedervorlage] = useState<{ x: number; y: number } | null>(null);

  // ── Fetch attachments ────────────────────────────────────────────────────

  const fetchAnhaenge = useCallback(async () => {
    setAnhaengeLoading(true);
    const { data } = await supabase
      .from('email_anhaenge')
      .select('id, dateiname, dateipfad, dateityp, dateigroesse, created_at, graph_message_id, graph_attachment_id, storage_path, rechnung_status, rechnung_fehler')
      .eq('email_id', email.id)
      .order('created_at', { ascending: true });
    setAnhaenge(data || []);
    setAnhaengeLoading(false);
  }, [email.id]);

  // ── Fetch Ablage-Vorschläge ───────────────────────────────────────────────

  const fetchVorschlaege = useCallback(async () => {
    if (!email?.id) return;
    setVorschlaege(await ladeVorschlaegeFuerEmail(email.id));
  }, [email?.id]);

  // ── Fetch thread ─────────────────────────────────────────────────────────

  const fetchThread = useCallback(async () => {
    if (!email.conversation_id) return;
    setThreadLoading(true);
    const { data } = await supabase
      .from('emails')
      .select('id, von, betreff, datum, richtung, gelesen')
      .eq('conversation_id', email.conversation_id)
      .neq('id', email.id)
      .order('datum', { ascending: true })
      .limit(30);
    setThreadEmails(data || []);
    setThreadLoading(false);
  }, [email.id, email.conversation_id]);

  // ── Body der geöffneten Mail einzeln nachladen ────────────────────────────
  const fetchBody = useCallback(async () => {
    // Falls der HTML-Body bereits am Objekt hängt (Caller liefert die volle Mail), direkt nutzen.
    // Die Liste liefert html_inhalt bewusst NICHT (undefined) -> dann nachladen.
    if (email.html_inhalt !== undefined) {
      setBody({ html: email.html_inhalt ?? null, text: email.text_inhalt ?? null });
      return;
    }
    setBody(null);
    setBodyLoading(true);
    const { data } = await supabase
      .from('emails')
      .select('html_inhalt, text_inhalt')
      .eq('id', email.id)
      .maybeSingle();
    setBody({ html: data?.html_inhalt ?? null, text: data?.text_inhalt ?? null });
    setBodyLoading(false);
  }, [email.id, email.html_inhalt, email.text_inhalt]);

  useEffect(() => {
    fetchAnhaenge();
    fetchThread();
    fetchVorschlaege();
    fetchBody();
  }, [fetchAnhaenge, fetchThread, fetchVorschlaege, fetchBody]);

  // ── Zuordnung search ─────────────────────────────────────────────────────

  const searchMieter = async (query: string) => {
    setMieterSearch(query);
    if (query.trim().length < 2) { setMieterResults([]); return; }
    const { data } = await supabase
      .from('mieter')
      .select('id, vorname, nachname')
      .or(`vorname.ilike.%${query}%,nachname.ilike.%${query}%`)
      .limit(5);
    setMieterResults(data || []);
  };

  const searchEigentuemer = async (query: string) => {
    setEigentuemerSearch(query);
    if (query.trim().length < 2) { setEigentuemerResults([]); return; }
    const { data } = await supabase
      .from('eigentuemer')
      .select('id, anrede, vorname, nachname, firma_name')
      .or(`vorname.ilike.%${query}%,nachname.ilike.%${query}%,firma_name.ilike.%${query}%`)
      .limit(5);
    setEigentuemerResults(data || []);
  };

  const searchObjekt = async (query: string) => {
    setObjektSearch(query);
    if (query.trim().length < 2) { setObjektResults([]); return; }
    const { data } = await supabase
      .from('objekte')
      .select('id, bezeichnung')
      .ilike('bezeichnung', `%${query}%`)
      .limit(5);
    setObjektResults(data || []);
  };

  const updateZuordnung = async (field: string, value: string | null) => {
    setZuordnungSaving(true);
    await supabase
      .from('emails')
      .update({ [field]: value, zuordnung_typ: 'manuell' })
      .eq('id', email.id);
    setZuordnungSaving(false);
    setMieterSearch('');
    setEigentuemerSearch('');
    setObjektSearch('');
    setMieterResults([]);
    setEigentuemerResults([]);
    setObjektResults([]);
    onUpdated();
  };

  // Zuständigkeits-Override (Team/Mitarbeiter) speichern; null = zurück auf „vom Objekt".
  const updateZustaendig = async (id: string | null, typ: 'mitarbeiter' | 'team' | null) => {
    setZuordnungSaving(true);
    await supabase.from('emails').update({ zustaendig_id: id, zustaendig_typ: typ }).eq('id', email.id);
    setZuordnungSaving(false);
    onUpdated();
  };

  // Teams + Mitarbeiter für den Zuständigkeits-Picker (einmalig).
  useEffect(() => {
    (async () => {
      const [t, m] = await Promise.all([
        supabase.from('teams').select('id, name').order('name'),
        supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true),
      ]);
      setZustTeams(t.data || []);
      setZustMembers(m.data || []);
    })();
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────────

  const toggleMarkiert = async () => {
    setActionLoading(true);
    await supabase.from('emails').update({ markiert: !email.markiert }).eq('id', email.id);
    setActionLoading(false);
    onUpdated();
  };

  const toggleGelesen = async () => {
    setActionLoading(true);
    await supabase.from('emails').update({ gelesen: !email.gelesen }).eq('id', email.id);
    setActionLoading(false);
    onUpdated();
  };

  const handleDelete = async () => {
    setDeleting(true);
    // #544: zuerst in den Ordner „Gelöscht“, von dort endgültig.
    const { data: getroffen, error: dbFehler } = email.geloescht_am
      ? await supabase.from('emails').delete().eq('id', email.id).select('id')
      : await supabase.from('emails').update({ geloescht_am: new Date().toISOString(), geloescht_von: userProfile?.id ?? null }).eq('id', email.id).select('id');
    // Ohne Recht filtert die DB still (0 Zeilen) — endgültig löschen dürfen nur Admins.
    const error = dbFehler ?? ((getroffen ?? []).length === 0 ? { message: email.geloescht_am ? 'Endgültig löschen dürfen nur Admins.' : 'Keine Berechtigung.' } : null);
    setDeleting(false);
    // Fehler im Dialog zeigen statt still zu schließen (#538).
    if (error) { setLoeschFehler(error.message); return; }
    setShowDeleteConfirm(false);
    onDeleted();
  };

  const handleDownloadAnhang = async (anhang: Anhang) => {
    // No-Bucket: Bytes ueber die Edge Function aus dem Postfach holen und als Datei herunterladen.
    try {
      const blob = await ladeAnhangBlob(anhang.id, anhang.dateityp);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = anhang.dateiname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      console.error('Anhang-Download fehlgeschlagen', (e as Error).message);
    }
  };

  // ── plainText aus E-Mail-Inhalt ──────────────────────────────────────────
  // Fast alle Mails kommen als HTML (text_inhalt ist dann leer). Früher wurden hier nur
  // die Tags entfernt und auf 500 Zeichen gekürzt — dadurch klebte alles in einer Zeile
  // und die Signatur am Ende fiel weg. mailKlartext erhält die Zeilenstruktur.

  const plainText = (body?.text ?? email.text_inhalt)
    || mailKlartext(body?.html ?? email.html_inhalt);

  // Thread-Mail auf-/zuklappen; beim Aufklappen den Body einmalig nachladen.
  const toggleThreadItem = (id: string) => {
    const willExpand = !threadOffen.has(id);
    setThreadOffen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    if (willExpand && !threadBodies[id]) {
      setThreadBodies((prev) => ({ ...prev, [id]: 'loading' }));
      supabase.from('emails').select('html_inhalt, text_inhalt').eq('id', id).maybeSingle()
        .then(({ data }) => setThreadBodies((prev) => ({ ...prev, [id]: { html: data?.html_inhalt ?? null, text: data?.text_inhalt ?? null } })));
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  // Rechnungs-Import je Anhang in Klartext. 'keine' bleibt stumm (Anhang ist schlicht keine
  // Rechnung), 'fehler' zeigt den Grund im Titel.
  const rechnungHinweis = (a: Anhang): { label: string; fg: string; titel?: string } | null => {
    switch (a.rechnung_status) {
      case 'rechnung': return { label: 'Als Rechnung erfasst', fg: 'var(--ok)' };
      case 'unklar': return { label: 'Im KI-Pool · prüfen', fg: '#8a63d2' };
      case 'duplikat': return { label: 'Bereits erfasst · nicht erneut gebucht', fg: 'var(--ink3)' };
      case 'fehler': return { label: 'Import fehlgeschlagen', fg: 'var(--bad)', titel: a.rechnung_fehler || undefined };
      default: return null;
    }
  };

  const recipientList = toEmpfaengerListe(email.an);
  const ccList = toEmpfaengerListe(email.cc);
  // Dieselbe Regel, die der Composer für die Cc-Vorbelegung nutzt — so verspricht der Knopf
  // genau das, was danach im Formular steht.
  const weitereEmpfaenger = antwortEmpfaenger(
    { von: email.von, an: recipientList, cc: ccList }, eigeneAdresse || '', true,
  ).cc.length;
  const absender = parseAbsender(email.von || '');
  const ini = initialen(email.von_name || `${absender.vorname} ${absender.nachname}`.trim() || email.von || '');
  const zustSelect = email.zustaendig_id ? (email.zustaendig_typ === 'team' ? 't:' + email.zustaendig_id : 'p:' + email.zustaendig_id) : '';
  const kh = mobil ? 44 : 38;
  const erledigtHinweis = erledigtText(email, erledigtName);

  return (
    <div data-email-sidebar className="flex flex-col" style={{ gap: 12, padding: mobil ? 10 : 14 }}>
      {/* ── Kopf: bleibt am Desktop beim Scrollen oben stehen (Aktionsleiste immer erreichbar); mobil wäre er höher als der halbe Schirm ── */}
      <div className={`immo-glas-stark flex flex-col ${mobil ? '' : 'sticky'}`} style={{ top: 0, zIndex: 5, borderRadius: 24, padding: 16, gap: 12, background: `var(--glass-strong), var(--bg)` }}>
        <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
          <Chip ton="neutral" icon={email.richtung === 'eingang' ? EnvelopeSimple : PaperPlaneTilt}>{email.richtung === 'eingang' ? 'Eingang' : 'Ausgang'}</Chip>
          <span style={{ fontSize: 11, fontWeight: 700, color: email.gelesen ? 'var(--ink3)' : 'var(--warn)' }}>{email.gelesen ? 'Gelesen' : 'Ungelesen'}</span>
          {email.markiert && <Star size={13} weight="fill" color="var(--warn)" aria-label="Markiert" />}
          <span className="flex-1" />
          {kopf && (
            <>
              <IconKnopf titel="Vorherige E-Mail (↑)" onClick={kopf.onVorherige} groesse={kh} aus={!kopf.onVorherige}><CaretUp size={16} weight="bold" /></IconKnopf>
              <IconKnopf titel="Nächste E-Mail (↓)" onClick={kopf.onNaechste} groesse={kh} aus={!kopf.onNaechste}><CaretDown size={16} weight="bold" /></IconKnopf>
              {!mobil && (
                <IconKnopf titel={kopf.vollbild ? 'Vollbild verlassen' : 'Vollbild'} onClick={kopf.onVollbild} groesse={kh} an={kopf.vollbild}>
                  {kopf.vollbild ? <CornersIn size={16} weight="bold" /> : <CornersOut size={16} weight="bold" />}
                </IconKnopf>
              )}
            </>
          )}
          <IconKnopf titel="Schließen" onClick={onClose} groesse={kh}><X size={18} weight="bold" /></IconKnopf>
        </div>

        <h2 className="m-0" style={{ fontSize: mobil ? 18 : 20, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25, overflowWrap: 'anywhere' }}>
          {email.betreff || '(Kein Betreff)'}
        </h2>

        {/* Zuständigkeit ganz oben: der Wert, der im Alltag am häufigsten umgestellt wird (User 2026-08-12). */}
        <label className="flex items-center flex-wrap" style={{ gap: 8 }}>
          <UserCheck size={15} color="var(--ink3)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Zuständig</span>
          <span className="relative flex" style={{ flex: '1 1 160px', minWidth: 0 }}>
            <select
              aria-label="Zuständig"
              value={zustSelect}
              disabled={zuordnungSaving}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) updateZustaendig(null, null);
                else if (v.startsWith('t:')) updateZustaendig(v.slice(2), 'team');
                else updateZustaendig(v.slice(2), 'mitarbeiter');
              }}
              className="w-full min-w-0 cursor-pointer"
              style={{ appearance: 'none', WebkitAppearance: 'none', height: mobil ? 44 : 40, padding: '0 34px 0 12px', borderRadius: 12, border: 'none', outline: 'none', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                background: email.zustaendig_id ? 'var(--acc-soft)' : 'var(--fill2)', color: 'var(--ink)', opacity: zuordnungSaving ? 0.5 : 1 }}
            >
              <option value="">
                {email.objekt?.zustaendig && email.objekt.zustaendig !== 'Global'
                  ? `Automatisch (${email.objekt.zustaendig})`
                  : 'Automatisch (Objekt)'}
              </option>
              <optgroup label="Mitarbeiter">
                {zustMembers.map(m => <option key={m.id} value={'p:' + m.id}>{m.full_name}</option>)}
              </optgroup>
              <optgroup label="Teams">
                {zustTeams.map(t => <option key={t.id} value={'t:' + t.id}>{t.name}</option>)}
              </optgroup>
            </select>
            <CaretDown size={12} weight="bold" color="var(--ink3)" className="absolute pointer-events-none" style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }} />
          </span>
          {zuordnungSaving && <CircleNotch size={13} className="animate-spin" color="var(--ink3)" />}
        </label>

        {/* Absender & Empfänger */}
        <div className="flex items-start" style={{ gap: 10 }}>
          <span aria-hidden className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: '50%', fontSize: 13, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>{ini}</span>
          <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2, fontSize: 13 }}>
            <span style={{ overflowWrap: 'anywhere' }}><span style={{ color: 'var(--ink3)' }}>Von</span> <b style={{ fontWeight: 700 }}>{email.von}</b></span>
            <span style={{ overflowWrap: 'anywhere' }}><span style={{ color: 'var(--ink3)' }}>An</span> {recipientList.join(', ')}</span>
            {ccList.length > 0 && <span style={{ overflowWrap: 'anywhere' }}><span style={{ color: 'var(--ink3)' }}>CC</span> {ccList.join(', ')}</span>}
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{formatRelativeDate(email.datum)} · {formatAbsoluteDate(email.datum)}</span>
          </div>
        </div>

        {/* Aktionen */}
        <div className="flex items-center flex-wrap" style={{ gap: 8, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
          <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
            <ImmoButton art="akzent" groesse={mobil ? 44 : 40} icon={ArrowBendUpLeft} onClick={onReply}>Antworten</ImmoButton>
            {/* Allen antworten: nur sinnvoll, wenn außer uns noch jemand An/Cc stand. */}
            {onReplyAll && weitereEmpfaenger > 0 && (
              <ImmoButton groesse={mobil ? 44 : 40} icon={ArrowBendDoubleUpLeft} onClick={onReplyAll} title={`Allen antworten (${weitereEmpfaenger} weitere im Cc)`}>Allen</ImmoButton>
            )}
            <ImmoButton groesse={mobil ? 44 : 40} icon={ArrowBendUpRight} onClick={onForward}>Weiterleiten</ImmoButton>
            {/* Wiedervorlage für eingegangene und – seit #552 – gesendete Mails, nicht für archivierte. */}
            {(email?.richtung === 'eingang' || email?.ordner === 'SENT') && !email?.archiviert_am && (
              <ImmoButton groesse={mobil ? 44 : 40} icon={Clock} title="Wiedervorlage"
                onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setShowWiedervorlage({ x: r.left, y: r.bottom + 6 }); }}
                style={email?.wiedervorlage_am ? { background: 'var(--acc-soft)', color: 'var(--acc)' } : undefined}>
                Wiedervorlage
              </ImmoButton>
            )}
            {/* #570: Termin aus der Mail – Datum/Uhrzeit aus dem Text, Objekt/Mieter/Eigentümer aus der Zuordnung */}
            {onCreateTermin && (
              <ImmoButton groesse={mobil ? 44 : 40} icon={CalendarPlus} title="Termin aus dieser E-Mail anlegen"
                onClick={() => onCreateTermin(terminAusMail(email, plainText || '', new Date()))}>
                Termin
              </ImmoButton>
            )}
          </div>
          <div className="flex items-center" style={{ gap: 4, marginLeft: 'auto' }}>
            {/* Erledigt direkt aus der geöffneten Mail (User-Wunsch 2026-08-05), nur für eingegangene Mails. */}
            {onToggleErledigt && email.richtung === 'eingang' && (
              <button type="button" onClick={onToggleErledigt} title={email.erledigt ? 'Als offen markieren' : 'Als erledigt markieren'}
                className="immo-hover-flaeche flex items-center cursor-pointer"
                style={{ height: kh, padding: '0 10px', gap: 6, borderRadius: 12, border: 'none', fontSize: 12, fontWeight: 700,
                  background: email.erledigt ? 'var(--ok-soft)' : 'transparent', color: email.erledigt ? 'var(--ok)' : 'var(--ink2)' }}>
                <CheckCircle size={18} weight={email.erledigt ? 'fill' : 'regular'} />
                {email.erledigt ? 'Erledigt' : 'Als erledigt'}
              </button>
            )}
            <IconKnopf titel={email.markiert ? 'Markierung entfernen' : 'Mit Stern markieren'} onClick={toggleMarkiert} groesse={kh} aus={actionLoading}>
              <Star size={18} weight={email.markiert ? 'fill' : 'regular'} color={email.markiert ? 'var(--warn)' : undefined} />
            </IconKnopf>
            <IconKnopf titel={email.gelesen ? 'Als ungelesen markieren' : 'Als gelesen markieren'} onClick={toggleGelesen} groesse={kh} aus={actionLoading}>
              {email.gelesen ? <EnvelopeSimple size={18} /> : <EnvelopeOpen size={18} />}
            </IconKnopf>
            <span aria-hidden style={{ width: 1, height: 22, background: 'var(--line)', margin: '0 4px' }} />
            <IconKnopf titel="E-Mail löschen" onClick={() => setShowDeleteConfirm(true)} groesse={kh} farbe="var(--bad)">
              <Trash size={18} />
            </IconKnopf>
          </div>
        </div>
        {/* #573: wer/wann/warum erledigt (protokolliert seit 28.10.2026) */}
        {erledigtHinweis && (
          <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>
            <CheckCircle size={14} weight="fill" color="var(--ok)" className="shrink-0" />{erledigtHinweis}
          </span>
        )}
      </div>

      {/* Kontakt aus E-Mail anlegen (unbekannter Absender) */}
      {onCreateKontakt && senderBekannt === false && (
        <div className="flex">
          <ImmoButton groesse={mobil ? 44 : 36} icon={UserPlus} onClick={() => onCreateKontakt(baueKontaktPrefill(email.von || '', email.von_name, plainText))}
            style={{ background: 'var(--acc-soft)', color: 'var(--acc)' }}>
            Kontakt hinzufügen
          </ImmoButton>
        </div>
      )}

      {/* Inhalt */}
      <Abschnitt label="Inhalt" offen={showInhalt} onToggle={() => setShowInhalt(!showInhalt)} mobil={mobil}>
        {bodyLoading && !body ? (
          <span className="flex items-center" style={{ gap: 8, fontSize: 13, color: 'var(--ink3)', padding: '8px 0' }}>
            <CircleNotch size={15} className="animate-spin" /> Inhalt wird geladen…
          </span>
        ) : body?.html ? (
          <EmailHtmlAnsicht html={body.html} emailId={email.id} />
        ) : body?.text ? (
          <span style={{ fontSize: 16, lineHeight: 1.65, whiteSpace: 'pre-wrap', maxWidth: '72ch', overflowWrap: 'anywhere' }}>{body.text}</span>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Kein Inhalt vorhanden</span>
        )}
      </Abschnitt>

      {/* KI-Analyse */}
      {email?.id && (
        <KiAnalysePanel
          emailId={email.id}
          kiStatus={email.ki_status ?? null}
          ticketKategorien={ticketKategorien}
          istSuperAdmin={userProfile?.role === 'super admin'}
          ticketRefresh={ticketRefresh}
          email={{ betreff: email.betreff, plainText, objekt_id: email.objekt_id, mieter_id: email.mieter_id, absender: email.von, datum: email.datum ? new Date(email.datum).toLocaleDateString('de-DE') : null, in_reply_to: email.in_reply_to ?? null }}
          onCreateTicket={onCreateTicket ? (v) => onCreateTicket(v) : undefined}
          userId={userProfile?.id ?? null}
          onChanged={onChanged}
          mobil={mobil}
        />
      )}

      {/* Anhänge */}
      {(anhaenge.length > 0 || anhaengeLoading) && (
        <Abschnitt label="Anhänge" anzahl={anhaenge.length} offen={showAnhaenge} onToggle={() => setShowAnhaenge(!showAnhaenge)} mobil={mobil}>
          {anhaengeLoading ? (
            <span className="flex justify-center" style={{ padding: 12 }}><CircleNotch size={16} className="animate-spin" color="var(--ink3)" /></span>
          ) : (
            <div className="flex flex-col" style={{ gap: 14 }}>
              {anhaenge.map((a) => {
                const d = anhangDarstellung(a.dateityp);
                const r = rechnungHinweis(a);
                return (
                  <div key={a.id} className="flex flex-col" style={{ gap: 8 }}>
                    <div className="flex items-center" style={{ gap: 10 }}>
                      <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--bg)' }}>
                        <d.icon size={20} weight="fill" color={d.fg} />
                      </span>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{a.dateiname}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{formatFileSize(a.dateigroesse)}</span>
                      </div>
                      <IconKnopf titel="An Kollegen senden" onClick={() => setAnKollegen(a)} groesse={kh}><PaperPlaneTilt size={18} weight="bold" /></IconKnopf>
                      <IconKnopf titel="Herunterladen" onClick={() => handleDownloadAnhang(a)} groesse={kh}><DownloadSimple size={18} weight="bold" /></IconKnopf>
                    </div>
                    {/* Was der Rechnungs-Import mit diesem Anhang gemacht hat — vor allem „Duplikat" war bisher unsichtbar. */}
                    {r && (
                      <span title={r.titel} className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: r.fg, paddingLeft: 50 }}>
                        <Receipt size={13} weight="fill" />{r.label}
                      </span>
                    )}
                    <div style={{ paddingLeft: mobil ? 0 : 50 }}>
                      <AnhangVorschau
                        anhangId={a.id}
                        dateiname={a.dateiname}
                        dateityp={a.dateityp}
                        hatGraphRef={(!!a.graph_message_id && !!a.graph_attachment_id) || !!a.storage_path}
                        mobil={mobil}
                      />
                      {vorschlaege[a.id] && vorschlaege[a.id].status !== 'freigegeben' && (
                        <AblageKarte
                          anhang={a}
                          vorschlag={vorschlaege[a.id]}
                          emailObjektId={email.objekt_id ?? null}
                          userId={userProfile?.id ?? null}
                          onErledigt={() => { fetchVorschlaege(); }}
                          mobil={mobil}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Abschnitt>
      )}

      {/* Zuordnung */}
      <Abschnitt label="Zuordnung" offen={showZuordnung} onToggle={() => setShowZuordnung(!showZuordnung)} mobil={mobil}>
        <div className="flex flex-col" style={{ gap: 8, borderRadius: 16, padding: 12, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Chip ton={email.zuordnung_typ === 'manuell' ? 'neutral' : 'info'} groesse="xs">{email.zuordnung_typ === 'manuell' ? 'Manuell' : 'Automatisch'}</Chip>
            {zuordnungSaving && <CircleNotch size={12} className="animate-spin" color="var(--ink3)" />}
          </div>
          <ZuordnungsZeile icon={User} label="Mieter" wert={email.mieter ? `${email.mieter.vorname} ${email.mieter.nachname}` : null} />
          <ZuordnungsZeile icon={Users} label="Eigentümer" wert={email.eigentuemer ? eigentuemerAnzeigename(email.eigentuemer) : null} />
          <ZuordnungsZeile icon={Buildings} label="Objekt" wert={email.objekt ? email.objekt.bezeichnung : null} />
          {/* Zuständigkeit steht seit 2026-08-12 oben im Kopf — hier nur der aktuelle Stand. */}
          <ZuordnungsZeile icon={UserCheck} label="Zuständig" wert={email.zustaendig_id
            ? (email.zustaendig_typ === 'team'
                ? (zustTeams.find(t => t.id === email.zustaendig_id)?.name || 'Team')
                : (zustMembers.find(m => m.id === email.zustaendig_id)?.full_name || 'Mitarbeiter'))
            : (email.objekt?.zustaendig && email.objekt.zustaendig !== 'Global'
                ? `vom Objekt: ${email.objekt.zustaendig}`
                : 'nicht zugewiesen')} />
        </div>

        <div className="flex">
          <ImmoButton groesse={mobil ? 44 : 36} icon={LinkSimple} onClick={() => setEditZuordnung(!editZuordnung)}>
            {editZuordnung ? 'Abbrechen' : 'Zuordnung ändern'}
          </ImmoButton>
        </div>

        {editZuordnung && (
          <div className="flex flex-col" style={{ gap: 12 }}>
            <SuchFeld label="Mieter suchen" wert={mieterSearch} onChange={searchMieter} platzhalter="Name eingeben..." mobil={mobil}
              treffer={mieterResults.map((m) => ({ id: m.id, label: `${m.vorname} ${m.nachname}` }))}
              onWahl={(id) => updateZuordnung('mieter_id', id)}
              entfernen={email.mieter_id ? { label: 'Mieter-Zuordnung entfernen', onClick: () => updateZuordnung('mieter_id', null) } : null} />
            <SuchFeld label="Eigentümer suchen" wert={eigentuemerSearch} onChange={searchEigentuemer} platzhalter="Name eingeben..." mobil={mobil}
              treffer={eigentuemerResults.map((e) => ({ id: e.id, label: eigentuemerAnzeigename(e) }))}
              onWahl={(id) => updateZuordnung('eigentuemer_id', id)}
              entfernen={email.eigentuemer_id ? { label: 'Eigentümer-Zuordnung entfernen', onClick: () => updateZuordnung('eigentuemer_id', null) } : null} />
            <SuchFeld label="Objekt suchen" wert={objektSearch} onChange={searchObjekt} platzhalter="Bezeichnung eingeben..." mobil={mobil}
              treffer={objektResults.map((o) => ({ id: o.id, label: o.bezeichnung }))}
              onWahl={(id) => updateZuordnung('objekt_id', id)}
              entfernen={email.objekt_id ? { label: 'Objekt-Zuordnung entfernen', onClick: () => updateZuordnung('objekt_id', null) } : null} />
          </div>
        )}
      </Abschnitt>

      {/* Thread / Konversation */}
      {email.conversation_id && threadEmails.length > 0 && (
        <Abschnitt label="Konversation" anzahl={threadEmails.length} offen={showThread} onToggle={() => setShowThread(!showThread)} mobil={mobil}>
          {threadLoading ? (
            <span className="flex justify-center" style={{ padding: 12 }}><CircleNotch size={16} className="animate-spin" color="var(--ink3)" /></span>
          ) : (
            <div className="flex flex-col" style={{ gap: 6 }}>
              {threadEmails.map((te) => {
                const offen = threadOffen.has(te.id);
                const teBody = threadBodies[te.id];
                return (
                  <div key={te.id} className="flex flex-col overflow-hidden" style={{ borderRadius: 16, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                    <button type="button" onClick={() => toggleThreadItem(te.id)} aria-expanded={offen}
                      className="flex flex-col w-full text-left cursor-pointer" style={{ gap: 4, padding: 12, border: 'none', background: 'transparent', color: 'var(--ink)', minHeight: 44 }}>
                      <span className="flex items-center w-full" style={{ gap: 6, fontSize: 12, fontWeight: 800 }}>
                        {te.richtung === 'eingang'
                          ? <EnvelopeSimple size={13} color={te.gelesen ? 'var(--ink3)' : 'var(--info)'} />
                          : <PaperPlaneTilt size={13} color="var(--ink3)" />}
                        <span className="flex-1 min-w-0 truncate">{te.von}</span>
                        <span style={{ fontWeight: 600, color: 'var(--ink3)' }}>{formatRelativeDate(te.datum)}</span>
                        <CaretDown size={12} weight="bold" color="var(--ink3)" style={{ transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                      </span>
                      <span className="truncate" style={{ fontSize: 13, color: 'var(--ink2)', paddingLeft: 19 }}>{te.betreff || '(Kein Betreff)'}</span>
                    </button>
                    {offen && (
                      <div style={{ borderTop: '1px solid var(--line)', padding: 12 }}>
                        {!teBody || teBody === 'loading' ? (
                          <span className="flex items-center" style={{ gap: 8, fontSize: 12, color: 'var(--ink3)' }}><CircleNotch size={13} className="animate-spin" /> Inhalt wird geladen…</span>
                        ) : teBody.html ? (
                          <EmailHtmlAnsicht html={teBody.html} emailId={te.id} />
                        ) : teBody.text ? (
                          <span style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{teBody.text}</span>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Kein Inhalt vorhanden</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Abschnitt>
      )}

      {showWiedervorlage && (
        <WiedervorlageWahl
          x={showWiedervorlage.x}
          y={showWiedervorlage.y}
          aktuellesDatum={email?.wiedervorlage_am}
          onWahl={(iso) => { onWiedervorlage?.(iso); setShowWiedervorlage(null); }}
          onAufheben={() => { onWiedervorlage?.(null); setShowWiedervorlage(null); }}
          onSchliessen={() => setShowWiedervorlage(null)}
        />
      )}

      {anKollegen && (
        <AnKollegenSendenDialog
          anhang={{ quelle: 'email', id: anKollegen.id, name: anKollegen.dateiname, typ: anKollegen.dateityp, groesse: anKollegen.dateigroesse,
            kontext: email.betreff ? `Mail: ${email.betreff}` : null }}
          onSchliessen={() => setAnKollegen(null)}
          onFertig={(m) => { setAnKollegen(null); showToast({ type: 'success', message: m }); }} />
      )}

      {showDeleteConfirm && (
        <BestaetigenDialog
          titel={email.geloescht_am ? 'Diese E-Mail endgültig löschen?' : 'Diese E-Mail löschen?'}
          text={loeschFehler ? `Löschen fehlgeschlagen: ${loeschFehler}` : email.geloescht_am
            ? 'Das lässt sich nicht rückgängig machen. In Outlook bleibt die Mail erhalten, sie wird aber nicht erneut übernommen.'
            : 'Sie wird in den Ordner „Gelöscht“ verschoben und lässt sich dort wiederherstellen.'}
          bestaetigenLabel="Löschen"
          laeuft={deleting}
          onAbbrechen={() => { setShowDeleteConfirm(false); setLoeschFehler(null); }}
          onBestaetigen={() => { void handleDelete(); }}
        />
      )}
    </div>
  );
};

// ── Bausteine des Lesefensters ─────────────────────────────────────────────

function IconKnopf({ titel, onClick, groesse, aus = false, an = false, farbe, children }: {
  titel: string; onClick?: () => void; groesse: number; aus?: boolean; an?: boolean; farbe?: string; children: React.ReactNode;
}) {
  return (
    <button type="button" title={titel} aria-label={titel} onClick={onClick} disabled={aus || !onClick}
      className="immo-hover-flaeche flex items-center justify-center shrink-0 cursor-pointer"
      style={{ width: groesse, height: groesse, borderRadius: 12, border: 'none', background: an ? 'var(--acc-soft)' : 'transparent', color: farbe ?? (an ? 'var(--acc)' : 'var(--ink2)'), opacity: aus ? 0.35 : 1 }}>
      {children}
    </button>
  );
}

/** Einklappbare Karte (Inhalt, Anhänge, Zuordnung, Konversation). */
function Abschnitt({ label, anzahl, offen, onToggle, mobil, children }: { label: string; anzahl?: number; offen: boolean; onToggle: () => void; mobil: boolean; children: React.ReactNode }) {
  return (
    <section className="immo-innen flex flex-col" style={{ borderRadius: 22, padding: mobil ? 12 : 16, gap: 10 }}>
      <button type="button" onClick={onToggle} aria-expanded={offen} className="flex items-center w-full cursor-pointer"
        style={{ border: 'none', background: 'transparent', padding: 0, minHeight: mobil ? 44 : 24, color: 'var(--ink3)' }}>
        <span className="flex-1 text-left" style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}{anzahl !== undefined && anzahl > 0 ? ` · ${anzahl}` : ''}
        </span>
        {offen ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
      </button>
      {offen && children}
    </section>
  );
}

function ZuordnungsZeile({ icon: I, label, wert }: { icon: Icon; label: string; wert: string | null }) {
  return (
    <div className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
      <I size={14} color="var(--ink3)" className="shrink-0" />
      <span className="shrink-0" style={{ width: 84, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      {wert ? <span className="truncate" style={{ fontWeight: 700 }}>{wert}</span> : <span style={{ color: 'var(--ink3)', fontStyle: 'italic' }}>Nicht zugeordnet</span>}
    </div>
  );
}

function SuchFeld({ label, wert, onChange, platzhalter, treffer, onWahl, entfernen, mobil }: {
  label: string; wert: string; onChange: (v: string) => void; platzhalter: string;
  treffer: { id: string; label: string }[]; onWahl: (id: string) => void;
  entfernen: { label: string; onClick: () => void } | null; mobil: boolean;
}) {
  return (
    <label className="flex flex-col" style={{ gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      <input value={wert} onChange={(e) => onChange(e.target.value)} placeholder={platzhalter}
        style={{ height: 44, padding: '0 14px', borderRadius: 14, border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: mobil ? 16 : 14, color: 'var(--ink)' }} />
      {treffer.length > 0 && (
        <div className="flex flex-col" style={{ borderRadius: 14, padding: 4, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          {treffer.map(t => (
            <button key={t.id} type="button" onClick={() => onWahl(t.id)} className="immo-hover-flaeche text-left cursor-pointer"
              style={{ minHeight: mobil ? 44 : 36, padding: '0 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: 600 }}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {entfernen && (
        <button type="button" onClick={entfernen.onClick} className="self-start cursor-pointer"
          style={{ minHeight: mobil ? 44 : 28, padding: 0, border: 'none', background: 'transparent', color: 'var(--bad)', fontSize: 12, fontWeight: 700 }}>
          {entfernen.label}
        </button>
      )}
    </label>
  );
}

function anhangDarstellung(mimeType: string | null): { icon: Icon; fg: string } {
  const typ = (mimeType || '').toLowerCase();
  if (typ.includes('pdf')) return { icon: FilePdf, fg: 'var(--bad)' };
  if (typ.startsWith('image')) return { icon: FileImage, fg: 'var(--info)' };
  if (typ.includes('word') || typ.includes('doc')) return { icon: FileDoc, fg: 'var(--info)' };
  if (typ.includes('sheet') || typ.includes('excel') || typ.includes('csv')) return { icon: FileXls, fg: 'var(--ok)' };
  if (typ.includes('text')) return { icon: FileText, fg: 'var(--ink2)' };
  return { icon: File, fg: 'var(--ink3)' };
}

export default EmailDetailsSidebar;
